import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PlainTextEditor from './PlainTextEditor';
import RichMentionEditor from './RichMentionEditor';
import UserSearchDropdown from './UserSearchDropdown';
import MentionErrorBoundary from './MentionErrorBoundary';
import useUserSearch from '../../hooks/useUserSearch';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useRichMentionEditor } from '../../utils/featureFlags';
import { 
  detectMentionTrigger, 
  formatMention, 
  replaceMentionText,
  parseMentions,
  getCursorPosition
} from '../../utils/mentionUtils';
import { 
  isMobileDevice, 
  getViewportHeight,
  scrollIntoViewWithKeyboard 
} from '../../utils/mobileUtils';
import '../../styles/mentions.css';

const UserMentionInput = ({
  value = '',
  onChange,
  placeholder = 'Enter your content...',
  maxLength = 5000,
  className = '',
  error = false,
  disabled = false,
  onBlur,
  autoFocus = false,
  onMentionsChange,
  forceRichEditor = null // Override for feature flag (for testing)
}) => {
  // Get current user for feature flag evaluation
  const { user } = useAuthContext();
  
  // Determine if rich editor should be used
  const featureFlagEnabled = useRichMentionEditor(user?.uid);
  const useRichEditor = forceRichEditor !== null ? forceRichEditor : featureFlagEnabled;
  // State
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, height: 300 });
  const [mentionTrigger, setMentionTrigger] = useState(null);
  const [, setCurrentMentions] = useState([]);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isNavigatingDropdown, setIsNavigatingDropdown] = useState(false);
  
  // Refs
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);
  const searchRef = useRef(null);
  
  // Detect if we're inside a modal
  const [isInsideModal, setIsInsideModal] = useState(false);
  
  // Mobile detection (memoized for performance)
  const isMobile = useMemo(() => isMobileDevice(), []);
  
  // Detect if we're inside a modal
  useEffect(() => {
    if (containerRef.current) {
      const modalContainer = containerRef.current.closest('[role="dialog"], .modal-container, .modal-backdrop');
      setIsInsideModal(!!modalContainer);
    }
  }, []);
  
  // User search hook with performance optimizations
  const {
    users,
    isLoading,
    error: searchError,
    showNoResults,
    search,
    clearSearch,
    retrySearch
  } = useUserSearch({
    debounceMs: isMobile ? 400 : 300, // Longer debounce on mobile
    maxResults: isMobile ? 6 : 8, // Fewer results on mobile
    minSearchLength: 0
  });

  // Keep search function in ref to avoid dependency issues
  searchRef.current = search;
  
  // Handle content changes
  const handleContentChange = useCallback((newContent) => {
    onChange?.(newContent);
    
    // Update current mentions for tracking
    const mentions = extractMentionsFromContent(newContent);
    setCurrentMentions(mentions);
    onMentionsChange?.(mentions);
    
    // Check for mention trigger
    if (editorRef.current) {
      const cursorPosition = editorRef.current.selectionStart || 0;
      const trigger = detectMentionTrigger(newContent, cursorPosition);
      
      if (trigger.isTriggered) {
        // Only search if the search term has actually changed
        const shouldSearch = !mentionTrigger || 
                            mentionTrigger.searchTerm !== trigger.searchTerm ||
                            !showDropdown;
        
        setMentionTrigger(trigger);
        
        if (shouldSearch) {
          searchRef.current?.(trigger.searchTerm);
        }
        
        // Calculate dropdown position for textarea
        const position = calculateDropdownPositionForTextarea();
        setDropdownPosition(prevPos => {
          // Only update if position actually changed
          if (prevPos.top !== position.top || prevPos.left !== position.left || prevPos.height !== position.height) {
            return position;
          }
          return prevPos;
        });
        setShowDropdown(true);
      } else {
        closeMentionDropdown();
      }
    }
  }, [onChange, onMentionsChange]);
  
  // Handle user selection from dropdown
  const handleUserSelect = useCallback((user) => {
    if (!mentionTrigger || !editorRef.current) return;
    
    if (useRichEditor && editorRef.current.insertMention) {
      // Use rich editor's insert mention method
      editorRef.current.insertMention(user, mentionTrigger.startPosition, mentionTrigger.startPosition + mentionTrigger.searchTerm.length + 1);
    } else {
      // Fallback to plain text editor logic
      const mentionText = formatMention(user);
      const cursorPosition = editorRef.current.selectionStart || 0;
      
      // Replace the @ trigger with the formatted mention
      const currentText = editorRef.current.value || '';
      const result = replaceMentionText(
        currentText,
        mentionTrigger.startPosition,
        cursorPosition,
        mentionText
      );
      
      // Update the textarea value
      editorRef.current.value = result.text;
      
      // Trigger content change
      onChange?.(result.text);
      
      // Set cursor position after the mention
      setTimeout(() => {
        editorRef.current.selectionStart = result.cursorPosition;
        editorRef.current.selectionEnd = result.cursorPosition;
        editorRef.current.focus();
      }, 0);
    }
    
    closeMentionDropdown();
  }, [mentionTrigger, onChange, useRichEditor]);
  
  // Close mention dropdown
  const closeMentionDropdown = useCallback(() => {
    setShowDropdown(false);
    setMentionTrigger(null);
    clearSearch();
  }, [clearSearch]);
  
  // Handle key events for mention dropdown
  const handleKeyDown = useCallback((e) => {
    if (showDropdown) {
      // Let the dropdown handle navigation keys and prevent them from triggering new searches
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        setIsNavigatingDropdown(true);
        // Reset the flag after a short delay
        setTimeout(() => setIsNavigatingDropdown(false), 100);
        // Don't prevent default - let the dropdown handle the navigation
        return;
      }
    }
  }, [showDropdown]);
  
  // Mobile keyboard detection
  useEffect(() => {
    if (!isMobile) return;
    
    const handleResize = () => {
      const currentHeight = getViewportHeight();
      setViewportHeight(currentHeight);
      
      // Detect keyboard visibility
      const screenHeight = window.screen.height;
      const keyboardThreshold = screenHeight * 0.75;
      const isKeyboardVisible = currentHeight < keyboardThreshold;
      
      setKeyboardVisible(isKeyboardVisible);
      
      // Adjust dropdown position when keyboard appears/disappears
      if (showDropdown && mentionTrigger) {
        setTimeout(() => {
          const position = calculateDropdownPositionForTextarea();
          setDropdownPosition(position);
        }, 100);
      }
    };
    
    // Use different event listeners based on browser support
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isMobile, showDropdown, mentionTrigger]);
  
  // Handle touch events for better mobile interaction
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      timestamp: Date.now()
    };
  }, [isMobile]);
  
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile || !touchStartRef.current) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
      timestamp: Date.now()
    };
    
    // Check if it's a tap (not a scroll)
    const deltaX = Math.abs(touchEnd.x - touchStartRef.current.x);
    const deltaY = Math.abs(touchEnd.y - touchStartRef.current.y);
    const deltaTime = touchEnd.timestamp - touchStartRef.current.timestamp;
    
    const isTap = deltaX < 10 && deltaY < 10 && deltaTime < 300;
    
    if (isTap && editorRef.current) {
      // Focus immediately without delay for better responsiveness
      editorRef.current.focus();
      
      // Scroll into view with keyboard compensation
      scrollIntoViewWithKeyboard(editorRef.current, 100);
    }
    
    touchStartRef.current = null;
  }, [isMobile]);
  
  // Simple dropdown positioning at the base of textarea
  const calculateDropdownPositionForTextarea = useCallback(() => {
    if (!containerRef.current || !editorRef.current) {
      return { top: 0, left: 0, height: 200 };
    }
    
    try {
      const textarea = editorRef.current;
      const containerRect = containerRef.current.getBoundingClientRect();
      const textareaRect = textarea.getBoundingClientRect();
      
      // Position dropdown directly below textarea - use consistent coordinate system
      // Both mobile and desktop use absolute positioning relative to container
      const top = textareaRect.bottom - containerRect.top + 5;
      const left = textareaRect.left - containerRect.left;
      
      // Calculate dropdown height based on available space
      const safetyMargin = 20;
      const availableSpace = window.innerHeight - textareaRect.bottom - safetyMargin;
      const optimalHeight = isMobile ? 150 : 200;
      const finalHeight = Math.max(100, Math.min(optimalHeight, availableSpace));
      
      // Ensure dropdown width fits on mobile
      const dropdownWidth = isMobile ? Math.min(textareaRect.width, window.innerWidth - 20) : textareaRect.width;
      
      // Adjust left position if dropdown would overflow on mobile
      let finalLeft = left;
      if (isMobile) {
        const containerLeft = containerRect.left;
        const dropdownRight = containerLeft + left + dropdownWidth;
        if (dropdownRight > window.innerWidth - 10) {
          finalLeft = Math.max(0, window.innerWidth - containerLeft - dropdownWidth - 10);
        }
      }
      
      return { 
        top, 
        left: finalLeft, 
        height: finalHeight 
      };
    } catch (error) {
      console.debug('Error calculating dropdown position:', error);
      return { 
        top: isMobile ? 60 : 40, 
        left: 0, 
        height: isMobile ? 150 : 200 
      };
    }
  }, [isMobile]);
  
  // Handle mention trigger detection
  const handleMentionTrigger = useCallback((trigger) => {
    if (trigger.isTriggered) {
      // Only search if the search term has actually changed
      const shouldSearch = !mentionTrigger || 
                          mentionTrigger.searchTerm !== trigger.searchTerm ||
                          !showDropdown;
      
      setMentionTrigger(trigger);
      
      if (shouldSearch) {
        searchRef.current?.(trigger.searchTerm);
      }
      
      // Calculate dropdown position
      const position = calculateDropdownPositionForTextarea();
      setDropdownPosition(prevPos => {
        // Only update if position actually changed
        if (prevPos.top !== position.top || prevPos.left !== position.left || prevPos.height !== position.height) {
          return position;
        }
        return prevPos;
      });
      setShowDropdown(true);
    } else {
      closeMentionDropdown();
    }
  }, [mentionTrigger, showDropdown]);

  // Handle textarea key events for mention detection (fallback for plain text editor)
  const handleTextareaKeyUp = useCallback((e) => {
    if (!editorRef.current || useRichEditor) return;
    
    // Skip if we're navigating the dropdown
    if (isNavigatingDropdown) return;
    
    const textarea = editorRef.current;
    const cursorPosition = textarea.selectionStart || 0;
    const content = textarea.value || '';
    
    const trigger = detectMentionTrigger(content, cursorPosition);
    handleMentionTrigger(trigger);
  }, [isNavigatingDropdown, useRichEditor, handleMentionTrigger]);
  
  // Extract mentions from content
  const extractMentionsFromContent = useCallback((content) => {
    return parseMentions(content);
  }, []);
  
  // Handle editor blur
  const handleEditorBlur = useCallback((e) => {
    // Delay closing dropdown to allow for user selection
    setTimeout(() => {
      if (!showDropdown) return;
      
      // Check if focus moved to dropdown
      const activeElement = document.activeElement;
      const dropdownElement = document.querySelector('[role="listbox"]');
      
      if (!dropdownElement?.contains(activeElement)) {
        closeMentionDropdown();
      }
    }, 150);
    
    onBlur?.(e);
  }, [showDropdown, closeMentionDropdown, onBlur]);
  
  return (
    <MentionErrorBoundary fallbackType="inline">
      <div 
        ref={containerRef} 
        className={`relative ${className}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="space-y-2">
          {useRichEditor ? (
            <RichMentionEditor
              ref={editorRef}
              value={value}
              onChange={handleContentChange}
              onBlur={handleEditorBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              maxLength={maxLength}
              error={error}
              disabled={disabled}
              autoFocus={autoFocus}
              className="w-full mention-editor"
              rows={isMobile ? 3 : 4}
              onMentionTrigger={handleMentionTrigger}
              onMentionsChange={onMentionsChange}
            />
          ) : (
            <PlainTextEditor
              ref={editorRef}
              value={value}
              onChange={handleContentChange}
              onBlur={handleEditorBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              maxLength={maxLength}
              error={error}
              disabled={disabled}
              autoFocus={autoFocus}
              isMobile={isMobile}
              className="w-full mention-editor"
              onKeyUp={handleTextareaKeyUp}
              rows={isMobile ? 3 : 4}
            />
          )}
        </div>
        
        {/* User Search Dropdown with Error Boundary */}
        <MentionErrorBoundary fallbackType="dropdown">
          <UserSearchDropdown
            users={users}
            isLoading={isLoading}
            error={searchError}
            showNoResults={showNoResults}
            onUserSelect={handleUserSelect}
            onClose={closeMentionDropdown}
            onRetry={retrySearch}
            position={dropdownPosition}
            isVisible={showDropdown}
            maxHeight={dropdownPosition.height}
            className={`${isMobile ? 'mobile-dropdown' : ''}`}
            searchStartTime={null}
            showLoadingState={isLoading}
            skeletonCount={isMobile ? 2 : 3}
            usePortal={isInsideModal}
            containerRef={containerRef}
          />
        </MentionErrorBoundary>
        
        {/* Mobile-specific styles */}
        {isMobile && (
          <style jsx>{`
            .mobile-dropdown {
              max-width: calc(100vw - 20px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
            .mobile-dropdown .overflow-y-auto {
              -webkit-overflow-scrolling: touch;
              scroll-behavior: smooth;
            }
          `}</style>
        )}
      </div>
    </MentionErrorBoundary>
  );
};

export default UserMentionInput;